import type { CatalogueItem } from '@croissants/contracts';
import { textIn } from '@croissants/contracts/projections';
import { ProductPhoto, pillWord } from '@croissants/ui';
import type { JSX } from 'react';
import { money } from '../lib/format';
import { MAX_LINE_QUANTITY } from '../state/basket';
import { useLocale } from '../state/locale';
import { MinusIcon, PlusIcon } from './icons';

/**
 * One catalogue line as a shelf tile (PRD GST-003).
 *
 * Every tile in a ROW is the same height, whatever its item has to say: a grid
 * of tiles that each size to their own copy reads as a jumble, and the eye
 * cannot run down a column of prices. The name takes as many lines as the
 * tallest name beside it, two at most (the subgrid on `.gw-tile`), and the
 * description reserves its two whether or not it fills them.
 *
 * The tile carries the photo, the name and the price. Everything else -
 * allergens, ingredients, the rest of the description - is on the
 * item view, one tap away, and the whole tile is the tap target (GST-003, PRD
 * 5.7: a tile that must also carry a full allergen list cannot be a fixed size,
 * and the line was truncated on exactly the items that needed it most).
 *
 * The add control sits ON the photo, above the tap target, so a thumb already
 * looking at the item can add it without opening anything.
 *
 * The price is the platform's sale price (PRD CAT-003); bakeries never set it.
 */
export function ItemCard({
  item,
  quantity,
  onAdd,
  onSetQuantity,
  onOpen,
  aboveTheFold = false,
}: {
  item: CatalogueItem;
  quantity: number;
  onAdd: () => void;
  onSetQuantity: (quantity: number) => void;
  onOpen: () => void;
  /** Above the fold on first paint: fetched at once rather than lazily. */
  aboveTheFold?: boolean;
}): JSX.Element {
  const { locale, t } = useLocale();
  /*
   * The words the platform authored, in THIS reader's language (NFR-006).
   *
   * The item arrives named in the session's language with every other shipped
   * language beside it, so the language switch moves this tile in the same
   * render as the copy around it - it used to sit in the old language until a
   * reissued token and a refetch had come back, about a second later.
   */
  const name = textIn(locale, item.nameByLocale, item.name);
  const description = textIn(locale, item.descriptionByLocale, item.description);
  const image = item.images[0];
  const soldOut = item.availability !== 'IN_STOCK';
  const ceiling =
    item.maxQuantityPerOrder === null
      ? MAX_LINE_QUANTITY
      : Math.min(item.maxQuantityPerOrder, MAX_LINE_QUANTITY);

  return (
    <article className={`gw-tile${soldOut ? ' gw-tile--soldout' : ''}`}>
      <div className="gw-tile__media">
        {/* Square, like every product photo on the platform; a missing one still
            holds its square, or the tile beside it lifts. */}
        <ProductPhoto
          className="gw-tile__image"
          src={image?.url}
          alt={image?.alt ?? ''}
          loading={aboveTheFold ? 'eager' : 'lazy'}
          placeholder={'\u{1F950}'}
        />

        {soldOut ? (
          /* The list's word (`availability`, #2004) on the photograph's own
             white label, amber as the pill is: a tile corner, not a badge. */
          <span className="gw-tile__soldout">
            {pillWord('availability', 'SOLD_OUT_TODAY', locale)}
          </span>
        ) : quantity === 0 ? (
          <button
            type="button"
            className="gw-tile__add"
            aria-label={t('menu.addLabel', { name })}
            onClick={onAdd}
          >
            <PlusIcon size={22} />
          </button>
        ) : (
          /*
           * In the basket, the same corner becomes minus / count / plus. The
           * count is a live region so a guest using a screen reader hears the
           * new quantity where they tapped (NFR-005), and each control names
           * the item - "plus" eleven times down a menu is not navigation.
           */
          <div className="gw-tile__stepper">
            <button
              type="button"
              className="gw-tile__step"
              aria-label={
                quantity === 1 ? t('basket.remove', { name }) : t('basket.decrease', { name })
              }
              onClick={() => onSetQuantity(quantity - 1)}
            >
              <MinusIcon size={18} />
            </button>
            <output className="gw-tile__count" aria-label={t('basket.quantity', { name })}>
              {quantity}
            </output>
            <button
              type="button"
              className="gw-tile__step"
              disabled={quantity >= ceiling}
              aria-label={t('basket.increase', { name })}
              onClick={() => onSetQuantity(quantity + 1)}
            >
              <PlusIcon size={18} />
            </button>
          </div>
        )}
      </div>

      <h3 className="gw-tile__name">{name}</h3>
      <p className="gw-tile__price">{money(item.price, item.currency, locale)}</p>
      <p className="gw-tile__description">{description}</p>

      {/*
        The tap target, stretched over the whole tile. A sibling rather than an
        ancestor, because a button cannot legally contain the add button that
        sits above it - and it is the last child so it covers everything except
        the controls that raise themselves over it.
      */}
      <button
        type="button"
        className="gw-tile__hit"
        aria-label={t('menu.openLabel', { name })}
        onClick={onOpen}
      />
    </article>
  );
}
